import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { CtaLink } from "@/components/common/Cta";
import { PipeRun } from "@/components/common/PipeRun";
import { PROJECTS } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { text: "Mechanical fit-out", cls: "text-ink" },
  { text: "for Ireland's", cls: "text-brand" },
  { text: "new homes & buildings", cls: "text-ink" },
];
const FEATURE = PROJECTS.find((p) => p.slug === "stocking-vale-north");

export const HeroPhoto = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateY = useTransform(sx, [-0.5, 0.5], [-5, 5]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [4, -4]);
  const tx = useTransform(sx, [-0.5, 0.5], [-16, 16]);
  const ty = useTransform(sy, [-0.5, 0.5], [-12, 12]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scrollY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} className="relative [perspective:1200px]" data-testid="hero-photo">
      <div className="mb-3 flex items-center gap-3 text-steel" aria-hidden>
        <span className="h-3 w-px bg-steel" /><span className="h-px flex-1 bg-steel/50" />
        <span className="label-mono">{FEATURE.units} units</span>
        <span className="h-px flex-1 bg-steel/50" /><span className="h-3 w-px bg-steel" />
      </div>
      <motion.div style={{ rotateX, rotateY }} className="corner-ticks relative aspect-[4/3] overflow-hidden border border-canvas-line lg:aspect-[4/5]">
        <motion.img src={FEATURE.image} alt={`${FEATURE.name}, ${FEATURE.location}`} style={{ x: tx, y: ty, translateY: scrollY }}
          className="absolute -inset-8 h-[calc(100%+4rem)] w-[calc(100%+4rem)] max-w-none object-cover" />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.12)]" />
        <span className="label-mono absolute left-3 top-3 bg-canvas/85 px-2 py-1 text-ink">{FEATURE.ref}</span>
      </motion.div>
      <p className="label-mono mt-3 text-steel">{FEATURE.name} · {FEATURE.client} · Completed</p>
    </div>
  );
};

export const Hero = () => (
  <section className="relative overflow-hidden cad-grid pt-32 pb-20 sm:pt-40 lg:min-h-[100svh] lg:pb-24" data-testid="home-hero">
    <PipeRun className="hidden opacity-70 lg:block" />
    <div className="relative mx-auto grid max-w-7xl items-end gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
      <div className="lg:col-span-7">
        <p className="label-mono text-brand">DWG RR-000 · Mechanical &amp; renewable contractors · Est. 2009</p>
        <h1 className="mt-8 font-display text-[2.85rem] font-extrabold uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl xl:text-[5.6rem]" data-testid="hero-headline">
          {LINES.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span className={`block ${l.cls}`} initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.15 + i * 0.13 }}>
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-steel sm:text-lg">
          Ramor Renewables deliver 1st and 2nd fix plumbing, heating, heat pumps and ventilation for developers and main contractors across Dublin and surrounding counties.
        </p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <CtaLink to="/contact" testid="hero-enquiry-btn">Send a project enquiry</CtaLink>
          <CtaLink to="/projects" variant="outline" testid="hero-projects-btn">View projects</CtaLink>
        </div>
      </div>
      <div className="lg:col-span-5">
        <HeroPhoto />
      </div>
    </div>
  </section>
);
